// @file Pinia store for the LTI app
import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import { FACE_WITH_ROLLING_EYES_EMOJI, OPEN_HANDS_ICON } from '@@/bits/emoji'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@@/bits/numbers'
import { HttpCode } from '@@/enums'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import type { Wall } from '@@/types'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum LtiScreen {
  Success = 'success',
  Error = 'error',
  OrgVerification = 'org_verification',
  Login = 'login',
  DeeplinkMenu = 'deeplink_menu',
}

export const useLtiStore = defineStore('ltiStore', () => {
  const globalAlertDialogStore = useGlobalAlertDialogStore()

  // State
  const currentScreen = ref<LtiScreen>(LtiScreen.OrgVerification)
  const ltiLaunchUrl = ref<string>('')
  const idToken = ref<string>('')
  const stateTokenTimeoutSeconds = ref<number>(900)
  const isAwaitingServerResponse = ref<boolean>(false)
  const platformIssuer = ref<string>('')

  // Error screen
  const errorScreenTitle = ref<string>('')
  const errorScreenBody = ref<string>('')
  const actionButtonUrl = ref<string | null>(null)
  const actionButtonText = ref<string | null>(null)

  // Success screen
  const successScreenTitle = ref<string>('')
  const successScreenBody = ref<string>('')

  // Org verification screen
  const subdomain = ref<string>('')
  const subdomainValidationMessage = ref<string>('')

  // Login screen
  const username = ref<string>('')
  const usernameValidationMessage = ref<string>('')
  const password = ref<string>('')
  const passwordValidationMessage = ref<string>('')
  const minimumPasswordLength = ref<number>(MIN_PASSWORD_LENGTH)
  const maximumPasswordLength = ref<number>(MAX_PASSWORD_LENGTH)
  const forgotPasswordUrl = ref<string>('')
  const linkDeploymentToken = ref<string>('')
  const tokenValidationMessage = ref<string>('')
  const dashboardSettingsUrl = ref<string>('')

  // Deeplink menu screen
  const deeplinkUrl = ref<string>('')
  const oauthStateToken = ref<string>('')
  const userDisplayName = ref<string>('')
  const avatar = ref<string>('')
  const tenantName = ref<string>('')
  const tenantLogo = ref<string>('')
  const walls = ref<Wall[]>([])
  const createNewPadletUrl = ref<string>('')
  const openDashboardUrl = ref<string>('')
  const deeplinkTimeoutSeconds = ref<number>(12)

  // Getters
  const xOrgVerificationScreen = computed((): boolean => currentScreen.value === LtiScreen.OrgVerification)
  const xLoginScreen = computed((): boolean => currentScreen.value === LtiScreen.Login)
  const xDeeplinkMenuScreen = computed((): boolean => currentScreen.value === LtiScreen.DeeplinkMenu)
  const xErrorScreen = computed((): boolean => currentScreen.value === LtiScreen.Error)
  const xSuccessScreen = computed((): boolean => currentScreen.value === LtiScreen.Success)
  const xActionButton = computed((): boolean => actionButtonUrl.value !== null && actionButtonText.value !== null)

  // Actions

  function initialize(payload: {
    currentScreen: LtiScreen
    ltiLaunchUrl?: string
    deeplinkUrl?: string
    idToken?: string
    oauthStateToken?: string
    stateTokenTimeoutSeconds?: number
    minimumPasswordLength?: number
    maximumPasswordLength?: number
    userDisplayName?: string
    username?: string
    avatar?: string
    tenantName?: string
    walls?: Wall[]
    createNewPadletUrl?: string
    openDashboardUrl?: string
    deeplinkTimeoutSeconds?: number
    errorScreenTitle?: string
    errorScreenBody?: string
    successScreenTitle?: string
    successScreenBody?: string
    actionButtonUrl?: string | null
    actionButtonText?: string | null
    platformIssuer?: string | null
  }): void {
    currentScreen.value = payload.currentScreen ?? currentScreen.value
    ltiLaunchUrl.value = payload.ltiLaunchUrl ?? ltiLaunchUrl.value
    deeplinkUrl.value = payload.deeplinkUrl ?? deeplinkUrl.value
    idToken.value = payload.idToken ?? idToken.value
    stateTokenTimeoutSeconds.value = payload.stateTokenTimeoutSeconds ?? stateTokenTimeoutSeconds.value
    oauthStateToken.value = payload.oauthStateToken ?? oauthStateToken.value
    minimumPasswordLength.value = payload.minimumPasswordLength ?? minimumPasswordLength.value
    maximumPasswordLength.value = payload.maximumPasswordLength ?? maximumPasswordLength.value
    userDisplayName.value = payload.userDisplayName ?? userDisplayName.value
    username.value = payload.username ?? username.value
    avatar.value = payload.avatar ?? avatar.value
    tenantName.value = payload.tenantName ?? tenantName.value
    walls.value = payload.walls ?? walls.value
    createNewPadletUrl.value = payload.createNewPadletUrl ?? createNewPadletUrl.value
    openDashboardUrl.value = payload.openDashboardUrl ?? openDashboardUrl.value
    deeplinkTimeoutSeconds.value = payload.deeplinkTimeoutSeconds ?? deeplinkTimeoutSeconds.value
    errorScreenTitle.value = payload.errorScreenTitle ?? errorScreenTitle.value
    errorScreenBody.value = payload.errorScreenBody ?? errorScreenBody.value
    successScreenTitle.value = payload.successScreenTitle ?? successScreenTitle.value
    successScreenBody.value = payload.successScreenBody ?? successScreenBody.value
    actionButtonUrl.value = payload.actionButtonUrl ?? actionButtonUrl.value
    actionButtonText.value = payload.actionButtonText ?? actionButtonText.value
    platformIssuer.value = payload.platformIssuer ?? platformIssuer.value
  }

  function setExpiryErrorScreen(): void {
    currentScreen.value = LtiScreen.Error
    errorScreenTitle.value = __('The page has expired.')
    errorScreenBody.value = __('Please close this page and open it again.')
  }

  function setSubdomain(value: string): void {
    subdomain.value = value.trim().toLocaleLowerCase()
  }

  function setSubdomainValidationMessage(message: string): void {
    subdomainValidationMessage.value = message
  }

  async function verifySubdomain(): Promise<void> {
    if (subdomain.value === '') {
      subdomainValidationMessage.value = __('Please fill out this field.')
      return
    }

    isAwaitingServerResponse.value = true
    try {
      const response: {
        oauthStateToken: string
        stateTokenTimeoutSeconds: number
        idToken: string
        currentScreen: LtiScreen
        name: string
        logo: string
        dashboardSettingsUrl: string
        forgotPasswordUrl: string
      } = await fetchJson(ltiLaunchUrl.value, {
        method: HTTPMethod.post,
        jsonData: {
          subdomain: subdomain.value,
          id_token: idToken.value,
          state: oauthStateToken.value,
        },
      })
      setCurrentScreen(response.currentScreen)
      oauthStateToken.value = response.oauthStateToken
      stateTokenTimeoutSeconds.value = response.stateTokenTimeoutSeconds
      idToken.value = response.idToken
      tenantName.value = response.name
      tenantLogo.value = response.logo
      dashboardSettingsUrl.value = response.dashboardSettingsUrl
      forgotPasswordUrl.value = response.forgotPasswordUrl
    } catch (e) {
      if (e.status === 422) {
        try {
          const parsedErrorMessage = JSON.parse(e.message)
          subdomainValidationMessage.value = parsedErrorMessage.error
          oauthStateToken.value = parsedErrorMessage.oauthStateToken
          idToken.value = parsedErrorMessage.idToken
        } catch (jsonException) {
          captureException(jsonException)
        }
      } else {
        captureFetchException(e, { source: 'LTIVerifySubdomain' })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  function setCurrentScreen(screen: LtiScreen): void {
    currentScreen.value = screen
  }

  function setUsername(value: string): void {
    username.value = value.trim()
  }

  function setUsernameValidationMessage(message: string): void {
    usernameValidationMessage.value = message
  }

  function setPassword(value: string): void {
    password.value = value
  }

  function setPasswordValidationMessage(value: string): void {
    passwordValidationMessage.value = value
  }

  async function loginWithPassword(): Promise<void> {
    if (username.value === '' || password.value === '') return

    if (password.value.length < minimumPasswordLength.value) {
      passwordValidationMessage.value = __('Password is too short')
      return
    }

    if (password.value.length > maximumPasswordLength.value) {
      passwordValidationMessage.value = __('Password is too long')
      return
    }

    try {
      isAwaitingServerResponse.value = true
      const response: {
        oauthStateToken: string
        idToken: string
        currentScreen: LtiScreen
      } = await fetchJson(ltiLaunchUrl.value, {
        method: HTTPMethod.post,
        jsonData: {
          subdomain: subdomain.value,
          email: username.value,
          password: password.value,
          id_token: idToken.value,
          state: oauthStateToken.value,
        },
      })
      initialize(response)
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        try {
          const parsedErrorMessage = JSON.parse(e.message)
          const error = parsedErrorMessage.error

          if (error.error_code === 'NO_USER') {
            usernameValidationMessage.value = error.email
          } else if (error.error_code === 'WRONG_PASSWORD') {
            passwordValidationMessage.value = error.password
          } else if (error.error_code === 'PASSWORD_LOGIN_DISABLED') {
            passwordValidationMessage.value = __('Password login is disabled for this account. Please use SSO to sign in.')
          } else if (error.error_code === 'LTI_LAUNCH_ERROR') {
            globalAlertDialogStore.openAlertDialog({
              ...ALERT_ICON,
              title: __('Unable to log in'),
              body: error.detail,
              closeButtonText: __('Okay'),
            })
          } else {
            usernameValidationMessage.value = error.email
            globalAlertDialogStore.openAlertDialog({
              ...FACE_WITH_ROLLING_EYES_EMOJI,
              title: __('Unable to log in'),
              body: error.email,
              closeButtonText: __('Okay'),
            })
          }
        } catch (jsonException) {
          captureException(jsonException)
        }
      } else {
        captureFetchException(e, { source: 'LTILoginWithPassword' })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  function setLinkDeploymentToken(value: string): void {
    linkDeploymentToken.value = value
  }

  function setTokenValidationMessage(message: string): void {
    tokenValidationMessage.value = message
  }

  async function loginWithToken(): Promise<void> {
    if (linkDeploymentToken.value === '') return

    try {
      isAwaitingServerResponse.value = true

      const response: {
        oauthStateToken: string
        idToken: string
        currentScreen: LtiScreen
      } = await fetchJson(ltiLaunchUrl.value, {
        method: HTTPMethod.post,
        jsonData: {
          subdomain: subdomain.value,
          token: linkDeploymentToken.value,
          id_token: idToken.value,
          state: oauthStateToken.value,
        },
      })
      initialize(response)
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        try {
          const parsedErrorMessage = JSON.parse(e.message)
          const error = parsedErrorMessage.error

          if (error.error_code === 'INVALID_TOKEN') {
            tokenValidationMessage.value = error.token
          } else if (error.error_code === 'LTI_LAUNCH_ERROR') {
            globalAlertDialogStore.openAlertDialog({
              ...ALERT_ICON,
              title: __('Unable to log in'),
              body: error.detail,
              closeButtonText: __('Okay'),
            })
          } else {
            tokenValidationMessage.value = error.token
            globalAlertDialogStore.openAlertDialog({
              ...FACE_WITH_ROLLING_EYES_EMOJI,
              title: __('Unable to log in'),
              body: error.token,
              closeButtonText: __('Okay'),
            })
          }
        } catch (jsonException) {
          captureException(jsonException)
        }
      } else {
        captureFetchException(e, { source: 'LTILoginWithToken' })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  function alertNeedToReopen(): void {
    globalAlertDialogStore.openAlertDialog({
      ...OPEN_HANDS_ICON,
      title: __('New padlets will not be shown'),
      body: __('To see newly added padlets, close this menu and open it again.'),
      closeButtonText: __('Okay'),
    })
  }

  return {
    // State
    stateTokenTimeoutSeconds,
    isAwaitingServerResponse,
    subdomain,
    subdomainValidationMessage,
    tenantLogo,
    tenantName,
    username,
    password,
    forgotPasswordUrl,
    usernameValidationMessage,
    passwordValidationMessage,
    tokenValidationMessage,
    linkDeploymentToken,
    dashboardSettingsUrl,
    userDisplayName,
    avatar,
    createNewPadletUrl,
    openDashboardUrl,
    deeplinkUrl,
    deeplinkTimeoutSeconds,
    walls,
    errorScreenTitle,
    errorScreenBody,
    successScreenTitle,
    successScreenBody,
    actionButtonUrl,
    actionButtonText,
    platformIssuer,
    // Getters
    xOrgVerificationScreen,
    xLoginScreen,
    xDeeplinkMenuScreen,
    xErrorScreen,
    xSuccessScreen,
    xActionButton,

    // Actions
    initialize,
    setExpiryErrorScreen,
    setSubdomain,
    setSubdomainValidationMessage,
    verifySubdomain,
    setCurrentScreen,
    setUsername,
    setUsernameValidationMessage,
    setPassword,
    setPasswordValidationMessage,
    loginWithPassword,
    setLinkDeploymentToken,
    setTokenValidationMessage,
    loginWithToken,
    alertNeedToReopen,
  }
})
